import { CardContent, CardHeader, Stack } from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import MaterialTypography from "../../../components/MaterialTypography";
import { useNavigate } from "react-router-dom";
import Table from "../../../components/Table";
import { useEffect, useState } from "react";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData, postData } from "../../../services";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import UploadFile from "../../Utils/UploadFile";
import InsurerStateFilterDialog from './components/InsurerStateFilterDialog.js';

const InsurerStateList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.insurerStateStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.insurerStateStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.insurerStateStore?.totalPages
  );

  const [isRefresh, setIsRefresh] = useState(false);
  const [stateData, setStateData] = useState([]);
  const [search, setSearch] = useState("");
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    iCountryId: "",
  });

  useEffect(() => {
    fetchStateList();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //List API
  const fetchStateList = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/insurer/state?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&iCountryId=${filterData?.iCountryId}`;
                        
      //for active and inactive status
      if(filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }

      const response = await fetchData(url);

      if (response?.success) {
        setStateData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
    } 
    catch (error) {
      console.error("Error Fetching State:", error);
      showSnackbar(error?.response?.data?.message, "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true)
    setUploadSuccess(false)

    try {
        const formData = new FormData()

        formData.append('file', file)

        const result = await postData(`/reference/motor/insurer/bulkUpload/insurer-state`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });        

        if (result?.success) {
            setUploadSuccess(true)
            showSnackbar(result?.message,"success");
            setOpenUploadFileDialog(false);
            setClearFileOnSuccess(true);
            fetchStateList(); 
        }
        else {
            setUploadSuccess(false);
            showSnackbar('Faiiled to upload file', "error")
        }

    }catch (error) {
      const errorMessage = error?.response?.data?.message;                
      console.error('Upload error:', error || errorMessage)
      showSnackbar(errorMessage, "error")
    } 
    finally {
      setFileUploading(false)
    }
  };

  //delete api function
  const handleDelete = async (row) => {
    setCircleLoading(true);
    try {
      const response = await deleteData(`/reference/insurer/state-delete`, {
        ids: [row?.id],
      });
      const responseMessage = response?.message;
      setIsRefresh(!isRefresh);
      if (response?.success) {
        showSnackbar(responseMessage, "success");
      } else {
        showSnackbar(responseMessage, "success");
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message, "error");
    } finally {
      setCircleLoading(false);
    }
  };

  //Table Mapping
  const stateListTable = {
    columns: [
      {
        key: "sno",
        label: "S.No.",
        renderCell: (row) => row.sno,
      },
      {
        key: "name",
        label: "State Name",
        renderCell: (row) => row.name,
      },
      {
        key: "code",
        label: "Code",
        renderCell: (row) => row.code,
      },
      {
        key: "stateCode",
        label: "State Code",
        renderCell: (row) => row.stateCode,
      },
      {
        key: "createdAt",
        label: "Created At",
        renderCell: (row) => row.createdAt,
      },
      {
        key: "isActive",
        label: "Status",
        renderCell: (row) => (
          <span>
            {row.isActive ? (
              <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
            ) : (
              <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
            )}
          </span>
        ),
      },
    ],
    rows: stateData?.map((state, index) => ({
      id: state?.id,
      sno: index + 1,
      name: state?.name || "",
      code: state?.code || "",
      stateCode: state?.stateCode || "",
      isActive: state?.isActive,
      createdAt: new Date(state?.createdAt).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    })),
  };

  const handleAddNewState = () => {
    navigate(`/insurer-master/insurer-state/add-state`);
  };

  //table actions
  const actions = {
    edit: true,
    view: true,
    delete: true,

    onEdit: (row) => {
      navigate(
        `/insurer-master/insurer-state/add-state?pageType=edit&id=${row.id}`
      );
    },
    onView: (row) => {
      navigate(
        `/insurer-master/insurer-state/add-state?pageType=view&id=${row.id}`
      );
    },
    onDelete: (row) => handleDelete(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                State List
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of State around the world.
              </MaterialTypography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewState()}
              >
                State
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Table
          columns={stateListTable?.columns}
          data={stateListTable?.rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No State found"
          handleSearch={handleSearch}
        />
      </CardContent>
      <UploadFile
      open={openUploadFileDialog}
      setOpen={setOpenUploadFileDialog}
      onUploadSuccess={handleFileUploadSuccess}
      fileTypes={['csv', 'xlsx']}
      fileUploading={fileUploading}
      uploadSuccess={uploadSuccess}
      setUploadSuccess={setUploadSuccess}
      setFileUploading={setFileUploading}
      clearFileOnSuccess={clearFileOnSuccess}
      />
      <InsurerStateFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
        setAnchorEl(null);   // close popup
        fetchStateList();  // call API after filter applied
      }}
      />
    </DashboardLayout>
  );
};

export default InsurerStateList;
